'use client'

import cn from 'classnames'
import type {PanInfo} from 'framer-motion'
import {AnimatePresence, motion} from 'framer-motion'
import type {ReactNode} from 'react'
import {useEffect, useId, useState} from 'react'

const DRAG_THRESHOLD_PX = 40
const SHEET_TRANSITION = {type: 'spring', damping: 32, stiffness: 320} as const

export type MobileResultsDrawerProps = {
  isVisible: boolean
  timeLabel: string
  timeValue: string
  moneyLabel: string
  moneyValue: string
  periodSuffix: string
  expandLabel: string
  collapseLabel: string
  headerAction: ReactNode
  children: ReactNode
}

export const MobileResultsDrawer = ({
  isVisible,
  timeLabel,
  timeValue,
  moneyLabel,
  moneyValue,
  periodSuffix,
  expandLabel,
  collapseLabel,
  headerAction,
  children,
}: MobileResultsDrawerProps) => {
  const sheetId = useId()
  const [isExpanded, setIsExpanded] = useState(false)
  const isOpen = isVisible && isExpanded

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y < -DRAG_THRESHOLD_PX) {
      setIsExpanded(true)
    }

    if (info.offset.y > DRAG_THRESHOLD_PX) {
      setIsExpanded(false)
    }
  }

  // Collapse the sheet once the drawer hides, so it never reappears already open
  useEffect(() => {
    if (!isVisible) {
      setIsExpanded(false)
    }
  }, [isVisible])

  // Close the open sheet with Escape and stop the page behind it from scrolling
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsExpanded(false)
      }
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="lg:hidden">
          {isExpanded && (
            <motion.div
              data-testid="results-drawer-backdrop"
              className="fixed inset-0 z-40 bg-moss-black/40"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              onClick={() => setIsExpanded(false)}
            />
          )}
          <motion.div
            data-testid="results-drawer"
            className="fixed inset-x-0 bottom-0 z-40 flex max-h-[85vh] flex-col rounded-t-2xl bg-green-dark pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_rgba(0,0,0,0.18)]"
            initial={{y: '100%'}}
            animate={{y: 0}}
            exit={{y: '100%'}}
            transition={SHEET_TRANSITION}
            drag="y"
            dragConstraints={{top: 0, bottom: 0}}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
          >
            {isExpanded && (
              <div className="flex flex-col items-center gap-3 pt-2.5">
                <span aria-hidden="true" className="h-1 w-10 rounded-full bg-moss-white/30" />
                {headerAction}
              </div>
            )}
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={sheetId}
              aria-label={isExpanded ? collapseLabel : expandLabel}
              onClick={() => setIsExpanded((expanded) => !expanded)}
              className={cn(
                'flex w-full flex-col items-center gap-3 px-5 pb-4 text-moss-white',
                isExpanded ? 'pt-4' : 'pt-2.5',
              )}
            >
              {!isExpanded && <span aria-hidden="true" className="h-1 w-10 rounded-full bg-moss-white/30" />}
              <span className="relative grid w-full grid-cols-2 gap-4 px-6 text-center">
                <span className="flex flex-col items-center gap-1">
                  <span className="text-12 leading-none text-moss-white/70">{timeLabel}</span>
                  <span className="text-24 font-medium leading-none">
                    {timeValue}
                    <span className="ml-1 text-14 font-normal text-moss-white/70">{periodSuffix}</span>
                  </span>
                </span>
                <span className="flex flex-col items-center gap-1">
                  <span className="text-12 leading-none text-moss-white/70">{moneyLabel}</span>
                  <span className="whitespace-nowrap text-24 font-medium leading-none">
                    {moneyValue}
                    <span className="ml-1 text-14 font-normal text-moss-white/70">{periodSuffix}</span>
                  </span>
                </span>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  className={cn(
                    'absolute right-0 top-1/2 size-4 -translate-y-1/2 transition-transform',
                    isExpanded && 'rotate-180',
                  )}
                >
                  <path
                    d="M4 10l4-4 4 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <div
              id={sheetId}
              hidden={!isExpanded}
              className={cn(
                'flex-col gap-3 overflow-y-auto overscroll-contain px-3 pb-3',
                isExpanded ? 'flex' : 'hidden',
              )}
            >
              <div className="overflow-hidden rounded-lg bg-moss-white">{children}</div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
